import React, { useEffect, useState } from "react";
import { X, Share2, Download, Loader2 } from "lucide-react";
import { renderProverbCard, shareProverbCard } from "../lib/proverbCard";
import { useMT } from "../lib/miniI18n";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { celebrateWin } from "../lib/celebrate";
import { toast } from "sonner";
import { fmt } from "../lib/format";

/* Tap a banner proverb → golden ROYAL LAKSHMI card preview with Share (WhatsApp/Status) + Save */
export function ProverbShareModal({ proverb, label, rtl, onClose }) {
  const [img, setImg] = useState("");
  const [busy, setBusy] = useState(false);
  const mt = useMT();
  const { user, loadUser } = useAuth();
  const [streak, setStreak] = useState(null);
  useEffect(() => { let on = true; renderProverbCard({ proverb, label, rtl }).then((u) => on && setImg(u)); return () => { on = false; }; }, [proverb, label, rtl]);
  useEffect(() => { if (user) api.get("/proverbs/streak").then((r) => setStreak(r.data)).catch(() => {}); }, [user]);
  const record = async () => {
    if (!user) return;
    try {
      const { data } = await api.post("/proverbs/shared");
      setStreak(data);
      if (data.bonus > 0) { celebrateWin(data.bonus, 0); toast.success(`🔥 7-day streak! ${fmt(data.bonus)} bonus added`); loadUser?.(); }
      else if (!data.already) toast.success(`🔥 ${mt("Streak")} ${data.streak}/${data.target}`);
    } catch {}
  };
  const save = () => { const a = document.createElement("a"); a.href = img; a.download = `royal-laxmi-proverb-${Date.now()}.png`; a.click(); record(); };
  const share = async () => { setBusy(true); try { await shareProverbCard({ proverb, label, rtl }); record(); } finally { setBusy(false); } };
  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="proverb-share-modal">
      <div className="w-full max-w-sm bg-slate-900 rounded-3xl p-3 border border-amber-300/30 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-1 mb-2">
          <div className="text-amber-200 text-xs font-bold tracking-widest">{label || "ROYAL LAKSHMI"}</div>
          <button onClick={onClose} className="text-white/70 p-1" data-testid="proverb-share-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="rounded-2xl overflow-hidden bg-slate-800 aspect-[4/5] flex items-center justify-center">
          {img ? <img src={img} alt="" className="w-full h-full object-contain" data-testid="proverb-card-img" /> : <Loader2 className="w-6 h-6 text-amber-300 animate-spin" />}
        </div>
        {streak && (
          <div className="mt-3 rounded-xl bg-orange-500/15 border border-orange-400/40 px-3 py-2 flex items-center gap-2" data-testid="proverb-streak">
            <div className="flex gap-1">{Array.from({ length: streak.target }).map((_, i) => <span key={i} className={`w-2.5 h-2.5 rounded-full ${i < streak.streak ? "bg-orange-400 shadow-[0_0_6px_rgba(251,146,60,.9)]" : "bg-white/15"}`} />)}</div>
            <div className="text-[11px] text-orange-100 flex-1"><b data-testid="proverb-streak-count">{streak.streak}/{streak.target}</b> {mt("days shared")}{streak.shared_today ? ` · ${mt("done today")} ✓` : ""}</div>
            <div className="text-[10px] font-black text-amber-300">{fmt(streak.bonus_amount)} {mt("bonus")}</div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2 mt-3">
          <button onClick={save} disabled={!img} className="rl-card py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 text-slate-800" data-testid="proverb-card-save"><Download className="w-4 h-4" />{mt("Save")}</button>
          <button onClick={share} disabled={!img || busy} className="bg-[#25D366] text-white py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-1.5" data-testid="proverb-card-share">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}{mt("Share to Status")}</button>
        </div>
      </div>
    </div>
  );
}
